import { memo, useCallback } from "react";
import {
    GRID_ACTIONS_COLUMN_TYPE,
    type GridRowIdGetter,
    type GridRowParams
} from "@mui/x-data-grid";
import { FilterVariant } from "consts/filters";
import { TableGridMode } from "consts/table";
import {
    Filters as WaMessagingFilters,
    MenuItem
} from "consts/waMessaging";
import { useTypedSelector } from "hooks";
import { selectConstants } from "features/general/selectors";
import { useWaTemplatesSetupsActions } from "features/waMessaging/actions";
import {
    selectIsWaMessagingResourceLoading,
    selectIsWaMessagingResourceUninitialized,
    selectWaMessagingResourceSlice
} from "features/waMessaging/selectors";
import type { WaTemplateSetup } from "features/waMessaging/types";
import { convertToOptions } from "util/transformers";
import Resource from "ui/layouts/Resource";
import Table from "ui/widgets/Table";
import WaTemplateSetupProvider from "./WaTemplateSetupProvider";
import WaTemplateSetupDetail from "./WaTemplateSetupDetail";
import useRowActions from "./Actions";
import Toolbar from "./Toolbar";

const TemplateSetupsResource = () => {
    const { getWaTemplatesSetups, downloadCsv } = useWaTemplatesSetupsActions();
    const getRowActions = useRowActions();
    const { ConsumerDomain } = useTypedSelector(selectConstants);

    const getRowId: GridRowIdGetter<WaTemplateSetup> = useCallback(
        ({ coreId }: WaTemplateSetup) => coreId as string,
        []
    );

    return (
        <Resource
            breadcrumbsProps={{
                heading: 'Template Setups',
                links: [{ name: '' }],
                sx: { mb: 0 }
            }}
        >
            <Table
                mode={TableGridMode.WaTemplatesSetups}
                selectIsTableUninitialized={selectIsWaMessagingResourceUninitialized(MenuItem.WaTemplatesSetups)}
                selectIsTableLoading={selectIsWaMessagingResourceLoading(MenuItem.WaTemplatesSetups)}
                selectTableDataSlice={selectWaMessagingResourceSlice(MenuItem.WaTemplatesSetups)}
                fetchTableData={getWaTemplatesSetups}
                downloadCsv={downloadCsv}
                filterTypes={new Map<FilterVariant, string[]>([
                    [FilterVariant.SingleOption, [WaMessagingFilters.MOR]]
                ])}
                getRowId={getRowId}
                Toolbar={Toolbar}
                columns={[
                    {
                        field: GRID_ACTIONS_COLUMN_TYPE,
                        type: GRID_ACTIONS_COLUMN_TYPE,
                        getActions: (params: GridRowParams) => getRowActions(params as GridRowParams<WaTemplateSetup>)
                    }
                ]}
                initialState={{
                    pinnedColumns: {
                        right: [GRID_ACTIONS_COLUMN_TYPE]
                    }
                }}
                LayoutProps={{ sx: { m: '24px 0px' } }}
                FiltersProps={{
                    sx: { m: 0 },
                    filtersModelOverrides: {
                        Domains: convertToOptions(ConsumerDomain ?? [])
                    }
                }}
            />
        </Resource>
    );
};

const TemplateSetups = () => (
    <WaTemplateSetupProvider>
        <TemplateSetupsResource />
        <WaTemplateSetupDetail />
    </WaTemplateSetupProvider>
);

export default memo(TemplateSetups);
